import type Transaction from "byzantine/src/Transaction";

/**
 * Combines internal and external transactions into the single flat list the
 * chart consumes.
 *
 * Both sources serialize through `TransactionSerializer`, so the row shapes are
 * already identical and no field mapping is required. Ids are uuid4 from two
 * different tables, so a collision is practically impossible -- the de-dupe is
 * defensive only, and cheap.
 *
 * Ordering mirrors the server's `-datetime, -id`: newest first, with `id` as a
 * stable tiebreaker. MX supplies date-only values for external rows, so
 * same-day entries collapse to a shared midnight timestamp and would otherwise
 * reshuffle between renders.
 */
export default function mergeTransactions(
  internal: Transaction[],
  external: Transaction[]
): Transaction[] {
  const byId = new Map<string, Transaction>();
  [...internal, ...external].forEach((txn) => {
    if (!byId.has(txn.id)) byId.set(txn.id, txn);
  });

  return [...byId.values()].sort((a, b) => {
    // Unsettled rows sink to the bottom rather than poisoning the comparison.
    const left = a.settled_at ?? "";
    const right = b.settled_at ?? "";
    if (left !== right) return left < right ? 1 : -1;
    return a.id < b.id ? 1 : -1;
  });
}
